import { FontColorEnum, FormField, FormHint, InputFile, FormLabel } from '@acceso/design-system';
import { Controller, FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FilesPicker } from '../FilesPicker/FilesPicker';

type TInputLogosProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  accept?: string;
  format?: string;
  maxSize?: number;
  defaultValue?: PathValue<T, Path<T>> | undefined;
  newFiles?: boolean;

  /**
   * @default false
   */
  singleFile?: boolean;
  hidePreview?: boolean;
};

export function InputLogos<T extends FieldValues>({
  form: { control },
  label,
  name,
  required,
  disabled,
  accept,
  defaultValue,
  newFiles,
  singleFile = false,
  maxSize,
  format,
  hidePreview,
}: TInputLogosProps<T>) {
  const { t } = useTranslation();
  const labelSuffix = required ? '*' : '';

  return (
    <FormField className="w-full !flex-row flex-wrap gap-size-24">
      <FormLabel htmlFor={name}>
        {label}
        {labelSuffix}
      </FormLabel>

      <Controller
        disabled={disabled}
        control={control}
        name={name}
        defaultValue={defaultValue}
        rules={{
          required: required ? { value: true, message: t('El campo es obligatorio') } : false,
        }}
        render={({ field: { onChange, value = [] }, fieldState: { error } }) => {
          const files = (value as unknown as File[]) ?? [];

          return (
            <div className="flex w-full flex-col gap-size-8">
              {newFiles && (
                <FilesPicker
                  value={value}
                  onChange={files => {
                    if (singleFile) {
                      onChange(files[0]);
                    } else {
                      onChange(files);
                    }
                  }}
                  singleFile={singleFile}
                  format={format}
                  maxSize={maxSize}
                  accept={accept}
                  // addFiles
                />
              )}
              {error && <FormHint color={FontColorEnum.Error20}>{error.message}</FormHint>}

              {!hidePreview && (
                <div className="flex flex-wrap gap-3">
                  {Array.isArray(files) &&
                    files.map((file, index) => (
                      <InputFile
                        label=""
                        placeholder=""
                        key={index}
                        onFileRemoved={() => {
                          const filteredFiles = files.filter(filteredFile => filteredFile !== file);
                          onChange(filteredFiles);
                        }}
                        classes={{
                          frame: '!max-w-[135px]',
                        }}
                        url={file instanceof File ? URL.createObjectURL(file) : file}
                        isImage
                      />
                    ))}
                </div>
              )}
            </div>
          );
        }}
      />
    </FormField>
  );
}
